/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LockIcon,
  EnvelopeIcon,
  PaperPlaneRightIcon,
  PlusIcon,
  CrownIcon,
  ArrowClockwiseIcon,
  BellIcon,
  SlackLogoIcon,
  DeviceMobileIcon,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { BACKEND_URL } from "@/lib/api";

const ADMIN_API = axios.create({ baseURL: `${BACKEND_URL}` });

const AdminPage = () => {
  const [token, setToken] = useState(localStorage.getItem("golucru_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [emailEdits, setEmailEdits] = useState({});
  const [busy, setBusy] = useState(null);
  const [quotaAdd, setQuotaAdd] = useState({ twitch: 50, cam: 50, other: 50 });
  const [notifCfg, setNotifCfg] = useState({ mode: "instant", slack_webhook_url: "", device_count: 0 });

  const api = (p, opts = {}) =>
    ADMIN_API.request({ url: p, headers: { "X-Admin-Token": token }, ...opts });

  const refresh = async () => {
    try {
      const [g, n] = await Promise.all([
        api("/api/admin/vip/grants"),
        api("/api/admin/notifications/config"),
      ]);
      setData(g.data);
      setNotifCfg(n.data);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
        toast.error("Invalid admin token");
      } else {
        toast.error("Failed to load");
      }
    }
  };

  useEffect(() => {
    if (token) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const auth = async (e) => {
    e?.preventDefault();
    if (!token.trim()) return;
    localStorage.setItem("golucru_admin_token", token.trim());
    await refresh();
  };

  const saveEmails = async () => {
    const entries = Object.entries(emailEdits)
      .filter(([, v]) => (v || "").includes("@"))
      .map(([session_id, email]) => ({ session_id, email }));
    if (entries.length === 0) return toast.info("No emails to save");
    setBusy("backfill");
    try {
      const r = await api("/api/admin/vip/backfill-emails", { method: "post", data: { entries } });
      toast.success(`Patched ${r.data.updated} emails`);
      setEmailEdits({});
      await refresh();
    } catch (e) {
      toast.error("Backfill failed");
    } finally {
      setBusy(null);
    }
  };

  const sendAllMissing = async () => {
    setBusy("send-all");
    try {
      const r = await api("/api/admin/vip/send-reminder", { method: "post", data: { all_missing: true } });
      toast.success(`Queued ${r.data.queued} reminder emails`);
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setBusy(null);
    }
  };

  const sendOne = async (sid) => {
    setBusy(`send-${sid}`);
    try {
      await api("/api/admin/vip/send-reminder", { method: "post", data: { session_ids: [sid] } });
      toast.success("Reminder queued");
      await refresh();
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setBusy(null);
    }
  };

  const addQuota = async (group) => {
    const add = parseInt(quotaAdd[group], 10);
    if (!add || add <= 0) return;
    setBusy(`quota-${group}`);
    try {
      const r = await api("/api/admin/vip/quota", { method: "post", data: { group, add } });
      toast.success(`+${add} slots → ${group} (now ${r.data.quotas[group]})`);
      await refresh();
    } catch (e) {
      toast.error("Quota update failed");
    } finally {
      setBusy(null);
    }
  };

  const saveNotif = async () => {
    setBusy("notif-save");
    try {
      const r = await api("/api/admin/notifications/config", { method: "post", data: notifCfg });
      setNotifCfg({ ...notifCfg, ...r.data });
      toast.success("Notifications settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(null);
    }
  };

  const testNotif = async () => {
    setBusy("notif-test");
    try {
      await api("/api/admin/notifications/test", { method: "post" });
      toast.success("Test fired — check email / Slack / phone");
    } finally {
      setBusy(null);
    }
  };

  const b64ToUint8 = (b64) => {
    const padding = "=".repeat((4 - (b64.length % 4)) % 4);
    const raw = window.atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  };

  const enablePush = async () => {
    try {
      setBusy("push");
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("Push not supported on this device");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.error("Permission denied");
          return;
        }
        const keyRes = await api("/api/push/public-key");
        const key = keyRes.data.public_key;
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64ToUint8(key),
        });
      }
      const payload = JSON.parse(JSON.stringify(sub));
      await api("/api/admin/push/subscribe", { method: "post", data: { subscription: payload, label: navigator.userAgent.slice(0, 60) } });
      toast.success("Push enabled on this device");
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error("Push setup failed");
    } finally {
      setBusy(null);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center p-6">
        <form onSubmit={auth} className="glass rounded-2xl p-8 max-w-sm w-full">
          <div className="flex items-center gap-2 mb-6">
            <Logo size={36} withBackground />
            <div className="flex flex-col leading-none">
              <span className="font-display font-black">LUCRÜ · ADMIN</span>
            </div>
          </div>
          <label className="text-xs text-white/50 flex items-center gap-1.5 mb-2">
            <LockIcon size={11} /> Admin token
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="paste ADMIN_TOKEN"
            data-testid="admin-token-input"
            className="w-full bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#00E5FF]"
          />
          <button type="submit" className="btn-pill btn-primary w-full justify-center mt-4" data-testid="admin-auth-button">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const camp = data?.campaign || {};
  const grants = data?.grants || [];
  const missingEmails = grants.filter((g) => !g.email).length;
  const unremindedWithEmail = grants.filter((g) => g.email && !g.reminder_sent_at).length;

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <header className="glass border-b border-[#222233] px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={32} withBackground />
          <div className="flex flex-col leading-none">
            <span className="font-display font-black">LUCRÜ · ADMIN</span>
          </div>
        </Link>
        <button onClick={refresh} className="btn-pill btn-ghost text-xs" data-testid="admin-refresh">
          <ArrowClockwiseIcon size={12} /> Refresh
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Notifications — Lyra's console */}
        <section className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BellIcon size={16} color="#39FF14" />
              <h2 className="font-display font-black text-sm uppercase tracking-widest">Owner notifications</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={testNotif} disabled={busy === "notif-test"} className="btn-pill btn-ghost text-xs" data-testid="notif-test">
                <PaperPlaneRightIcon size={11} /> Test
              </button>
              <button onClick={saveNotif} disabled={busy === "notif-save"} className="btn-pill btn-primary text-xs" data-testid="notif-save">
                Save
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-[#05050A] border border-[#222233] rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Delivery mode</div>
              <div className="flex items-center gap-2">
                {["instant", "digest"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setNotifCfg({ ...notifCfg, mode: m })}
                    data-testid={`notif-mode-${m}`}
                    className={`flex-1 py-2 rounded-lg text-xs capitalize border transition-colors ${
                      notifCfg.mode === m ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/5" : "border-[#222233] text-white/60"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-white/40 mt-2">
                Digest = one summary email per day at 08:50 UTC. Instant = ping on every event.
              </p>
            </div>

            <div className="bg-[#05050A] border border-[#222233] rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
                <SlackLogoIcon size={11} /> Slack / Discord webhook URL
              </div>
              <input
                value={notifCfg.slack_webhook_url || ""}
                onChange={(e) => setNotifCfg({ ...notifCfg, slack_webhook_url: e.target.value })}
                placeholder="https://hooks.slack.com/..."
                data-testid="notif-slack-url"
                className="w-full bg-[#0F0F16] border border-[#222233] rounded px-2 py-1.5 text-xs font-mono outline-none focus:border-[#39FF14]"
              />
              <p className="text-[10px] text-white/40 mt-2">
                Works with Slack Incoming Webhooks and Discord's Slack-compatible webhook URL.
              </p>
            </div>

            <div className="bg-[#05050A] border border-[#222233] rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1.5">
                <DeviceMobileIcon size={11} /> Push on this device
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-black text-xl text-[#00E5FF]">
                    {notifCfg.device_count ?? 0}
                  </div>
                  <div className="text-[10px] text-white/40">devices receiving pushes</div>
                </div>
                <button
                  onClick={enablePush}
                  disabled={busy === "push"}
                  data-testid="notif-push-enable"
                  className="btn-pill btn-primary text-xs"
                >
                  Enable here
                </button>
              </div>
              <p className="text-[10px] text-white/40 mt-2">
                Install the PWA first, then tap "Enable here". iOS 16.4+ required.
              </p>
            </div>
          </div>
        </section>

        {/* Group quotas */}
        <section className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <CrownIcon size={16} color="#FF007F" />
            <h2 className="font-display font-black text-sm uppercase tracking-widest">Group quotas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["twitch", "cam", "other"].map((g) => {
              const total = camp.quotas?.[g] ?? 0;
              const claimed = camp.claimed_by_group?.[g] ?? 0;
              const remaining = Math.max(0, total - claimed);
              return (
                <div key={g} className="bg-[#05050A] border border-[#222233] rounded-xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{g}</div>
                  <div className="font-display font-black text-2xl">
                    {remaining}<span className="text-white/30 text-sm"> / {total}</span>
                  </div>
                  <div className="text-[11px] text-white/50 mb-3">{claimed} claimed</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={quotaAdd[g]}
                      onChange={(e) => setQuotaAdd({ ...quotaAdd, [g]: e.target.value })}
                      data-testid={`quota-input-${g}`}
                      className="w-20 bg-[#0F0F16] border border-[#222233] rounded px-2 py-1 text-xs"
                    />
                    <button
                      onClick={() => addQuota(g)}
                      disabled={busy === `quota-${g}`}
                      data-testid={`quota-add-${g}`}
                      className="btn-pill btn-primary text-xs px-3 py-1 disabled:opacity-50"
                    >
                      <PlusIcon size={11} /> Add
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Email backfill + bulk send */}
        <section className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <EnvelopeIcon size={16} color="#00E5FF" />
              <h2 className="font-display font-black text-sm uppercase tracking-widest">Backfill & reminders</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-white/50">
                <span className="text-[#FF007F] font-semibold">{missingEmails}</span> missing email ·{" "}
                <span className="text-[#39FF14] font-semibold">{unremindedWithEmail}</span> ready
              </div>
              <button
                onClick={saveEmails}
                disabled={busy === "backfill"}
                data-testid="save-emails"
                className="btn-pill btn-ghost text-xs disabled:opacity-50"
              >
                Save emails
              </button>
              <button
                onClick={sendAllMissing}
                disabled={busy === "send-all"}
                data-testid="send-all-missing"
                className="btn-pill btn-primary text-xs disabled:opacity-50"
              >
                <PaperPlaneRightIcon size={11} weight="fill" /> Send to all
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="grants-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-[#222233]">
                  <th className="py-2 px-2">#</th>
                  <th className="py-2 px-2">Streamer</th>
                  <th className="py-2 px-2">Group</th>
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2 text-right">Refs · Bonus</th>
                  <th className="py-2 px-2">Expires</th>
                  <th className="py-2 px-2">Reminder</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.session_id} className="border-b border-[#222233] hover:bg-white/5">
                    <td className="py-2 px-2 font-mono text-white/50">#{g.position}</td>
                    <td className="py-2 px-2 font-semibold">{g.streamer_name}</td>
                    <td className="py-2 px-2 text-white/60">{g.platform_group}</td>
                    <td className="py-2 px-2">
                      <input
                        value={emailEdits[g.session_id] ?? g.email ?? ""}
                        onChange={(e) =>
                          setEmailEdits({ ...emailEdits, [g.session_id]: e.target.value })
                        }
                        placeholder="—"
                        data-testid={`email-${g.session_id}`}
                        className="w-full bg-transparent border-b border-transparent focus:border-[#00E5FF] outline-none"
                      />
                    </td>
                    <td className="py-2 px-2 text-right">
                      <span className="text-[#39FF14] font-semibold">+{g.referral_count}</span>
                      <span className="text-white/40"> · </span>
                      <span className="text-[#00E5FF] font-semibold">+{g.bonus_days}d</span>
                    </td>
                    <td className="py-2 px-2 text-white/60">
                      {g.vip_until ? new Date(g.vip_until).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 px-2 text-[10px]">
                      {g.reminder_sent_at ? (
                        <span className="text-[#39FF14]">sent</span>
                      ) : g.email ? (
                        <span className="text-white/50">pending</span>
                      ) : (
                        <span className="text-[#FF007F]">no email</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <button
                        onClick={() => sendOne(g.session_id)}
                        disabled={!g.email || busy === `send-${g.session_id}`}
                        data-testid={`send-one-${g.session_id}`}
                        className="text-[11px] text-[#00E5FF] disabled:opacity-30"
                      >
                        send
                      </button>
                    </td>
                  </tr>
                ))}
                {grants.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-white/40">No grants yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminPage;
